import type { SVGProps } from 'react';

/**
 * Иконки нарисованы здесь, а не подключены пакетом, по двум причинам. Первая:
 * у пакета админки ровно две зависимости, и ради полутора десятков глифов
 * заводить третью незачем. Вторая: интерфейс работает на объекте, где интернета
 * может не быть, — всё, что рисуется, обязано лежать в бандле.
 *
 * Общая сетка 24×24, обводка `currentColor` толщиной 1.7 — иконка наследует цвет
 * и размер от текста рядом, поэтому в тёмной теме ничего не правится отдельно.
 */
export type IconProps = Omit<SVGProps<SVGSVGElement>, 'viewBox' | 'fill'> & {
  readonly size?: number;
};

function Icon({ size = 18, children, ...props }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      {...props}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/* --------------------------------------------------------------- разделы */

export function IconIdentity(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 17.5V19" />
      <circle cx="10" cy="8" r="3.2" />
      <path d="M19 19v-1.4a3.4 3.4 0 0 0-2.6-3.3M15.4 5.2a3.2 3.2 0 0 1 0 5.6" />
    </Icon>
  );
}

export function IconLpr(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="2.6" y="6.5" width="18.8" height="11" rx="2" />
      <path d="M6.5 10.5v3M9.6 10.5v3M12.7 10.5v3M15.8 10.5v3M18.6 10.5v3" />
    </Icon>
  );
}

export function IconLoyalty(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="m12 3.6 2.55 5.17 5.7.83-4.13 4.02.98 5.68L12 16.62l-5.1 2.68.97-5.68-4.12-4.02 5.7-.83z" />
    </Icon>
  );
}

export function IconBilling(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M20.4 12a8.4 8.4 0 0 1-14.6 5.6M3.6 12a8.4 8.4 0 0 1 14.6-5.6" />
      <path d="M18.4 3.2v3.4H15M5.6 20.8v-3.4H9" />
    </Icon>
  );
}

export function IconFeedback(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M20.5 13.6a2.4 2.4 0 0 1-2.4 2.4H8.2L4 19.6V6a2.4 2.4 0 0 1 2.4-2.4h11.7A2.4 2.4 0 0 1 20.5 6z" />
      <path d="M8.6 8.6h7M8.6 11.8h4.4" />
    </Icon>
  );
}

export function IconPos(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M5 20.6V4.8a1.2 1.2 0 0 1 1.8-1l1.6.9 1.8-1 1.8 1 1.8-1 1.8 1 1.6-.9a1.2 1.2 0 0 1 1.8 1v15.8l-2-1.1-1.8 1-1.8-1-1.8 1-1.8-1-1.8 1z" />
      <path d="M9 9.4h6M9 13h4" />
    </Icon>
  );
}

export function IconAnalytics(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M4 20h16" />
      <path d="M7 20v-6.4M12 20V6.5M17 20v-9.4" />
    </Icon>
  );
}

export function IconPayments(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="2.8" y="5.4" width="18.4" height="13.2" rx="2.2" />
      <path d="M2.8 10h18.4M6.4 14.6h3.2" />
    </Icon>
  );
}

export function IconComms(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M20.6 3.8 3.9 10.2a.5.5 0 0 0 .05.94l6.9 2.02 2.02 6.9a.5.5 0 0 0 .94.05z" />
      <path d="m10.85 13.16 4.6-4.6" />
    </Icon>
  );
}

export function IconBridge(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M3.6 8.4h13.2M13.6 5.2l3.2 3.2-3.2 3.2" />
      <path d="M20.4 15.6H7.2M10.4 12.4l-3.2 3.2 3.2 3.2" />
    </Icon>
  );
}

export function IconOps(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M2.8 12.4h4l2-5.6 3.4 10.4 2.2-4.8h6.8" />
    </Icon>
  );
}

export function IconStaff(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M9.5 11.2a3.1 3.1 0 1 0 0-6.2 3.1 3.1 0 0 0 0 6.2Z" />
      <path d="M3.2 19.4c0-3.1 2.8-5.2 6.3-5.2s6.3 2.1 6.3 5.2" />
      <path d="M16.4 5.4a2.7 2.7 0 0 1 0 5.3" />
      <path d="M18.2 14.6c1.7.6 2.8 1.9 2.8 3.6" />
    </Icon>
  );
}

/** Раздел без собственного глифа — точка вместо пустоты, чтобы строка не «прыгала». */
export function IconDot(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="3.2" />
    </Icon>
  );
}

export function IconAudit(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M6 3.4h8.6L19 7.8v12.8H6z" />
      <path d="M14.2 3.6v4.3h4.3" />
      <path d="M9 12.4h6M9 15.8h4" />
    </Icon>
  );
}

/** Объект сети: навес мойки над въездом. */
export function IconSites(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M3.4 10.6 12 4.6l8.6 6" />
      <path d="M5.4 10.4v9.2h13.2v-9.2" />
      <path d="M9.2 19.6v-5h5.6v5" />
    </Icon>
  );
}

const MODULE_ICONS: Readonly<Record<string, (p: IconProps) => React.JSX.Element>> = {
  identity: IconIdentity,
  lpr: IconLpr,
  loyalty: IconLoyalty,
  billing: IconBilling,
  feedback: IconFeedback,
  pos: IconPos,
  analytics: IconAnalytics,
  payments: IconPayments,
  comms: IconComms,
  bridge: IconBridge,
  ops: IconOps,
  staff: IconStaff,
  audit: IconAudit,
  sites: IconSites,
};

export function ModuleIcon({ module, ...props }: IconProps & { module: string }) {
  const Glyph = MODULE_ICONS[module] ?? IconDot;
  return <Glyph {...props} />;
}

/* ---------------------------------------------------------------- обвязка */

export function IconMenu(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Icon>
  );
}

export function IconClose(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M6.2 6.2 17.8 17.8M17.8 6.2 6.2 17.8" />
    </Icon>
  );
}

export function IconSun(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.8v2.1M12 19.1v2.1M4.5 4.5 6 6M18 18l1.5 1.5M2.8 12h2.1M19.1 12h2.1M4.5 19.5 6 18M18 6l1.5-1.5" />
    </Icon>
  );
}

export function IconMoon(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M20 13.4A8.2 8.2 0 0 1 10.6 4a8.4 8.4 0 1 0 9.4 9.4" />
    </Icon>
  );
}

export function IconMonitor(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="2.8" y="4.4" width="18.4" height="12.2" rx="2" />
      <path d="M8.6 20.2h6.8M12 16.6v3.6" />
    </Icon>
  );
}

export function IconChevronDown(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="m6.5 9.5 5.5 5.5 5.5-5.5" />
    </Icon>
  );
}

export function IconChevronLeft(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="m14.5 5.5-6 6.5 6 6.5" />
    </Icon>
  );
}

export function IconChevronRight(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="m9.5 5.5 6 6.5-6 6.5" />
    </Icon>
  );
}

export function IconCheck(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="m4.8 12.6 4.6 4.6 9.8-10.4" />
    </Icon>
  );
}

export function IconCopy(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="9" y="9" width="11.4" height="11.4" rx="2" />
      <path d="M15 6.4V5.6A2 2 0 0 0 13 3.6H5.6a2 2 0 0 0-2 2V13a2 2 0 0 0 2 2h.8" />
    </Icon>
  );
}

export function IconLogout(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M9.6 20.4H6a2 2 0 0 1-2-2V5.6a2 2 0 0 1 2-2h3.6" />
      <path d="M15.4 16.4 19.8 12l-4.4-4.4M19.4 12H9.2" />
    </Icon>
  );
}

export function IconSite(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M3.4 20.4h17.2M5.4 20.4V6.2l6.6-2.6 6.6 2.6v14.2" />
      <path d="M9.6 20.4v-4.2h4.8v4.2M9.4 9.4h1.6M13 9.4h1.6M9.4 12.6h1.6M13 12.6h1.6" />
    </Icon>
  );
}

export function IconWarning(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M12 4.4 2.9 20.1h18.2z" />
      <path d="M12 10v4.2M12 17.2h.01" />
    </Icon>
  );
}

export function IconInfo(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M12 11.4v5M12 8.1h.01" />
    </Icon>
  );
}

export function IconError(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M9 9l6 6M15 9l-6 6" />
    </Icon>
  );
}

export function IconRefresh(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M20.4 12a8.4 8.4 0 1 1-2.46-5.94" />
      <path d="M20.6 3.6v4.8h-4.8" />
    </Icon>
  );
}

/** Знак сети: капля. Мойка узнаётся по ней быстрее, чем по любой аббревиатуре. */
export function IconDrop(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M12 3.2c3.4 4 5.6 6.9 5.6 9.6a5.6 5.6 0 1 1-11.2 0c0-2.7 2.2-5.6 5.6-9.6Z" />
      <path d="M9.4 13.6a2.6 2.6 0 0 0 2.6 2.6" />
    </Icon>
  );
}

export function IconUser(p: IconProps) {
  return (
    <Icon {...p}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.8 20a7.2 7.2 0 0 1 14.4 0" />
    </Icon>
  );
}

export function IconLock(p: IconProps) {
  return (
    <Icon {...p}>
      <rect x="4.8" y="10.4" width="14.4" height="9.8" rx="2.4" />
      <path d="M8.2 10.4V7.2a3.8 3.8 0 0 1 7.6 0v3.2" />
    </Icon>
  );
}

export function IconEye(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M2.4 12S6 5.8 12 5.8 21.6 12 21.6 12 18 18.2 12 18.2 2.4 12 2.4 12Z" />
      <circle cx="12" cy="12" r="2.8" />
    </Icon>
  );
}

export function IconEyeOff(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M4 4.4 20 20.4" />
      <path d="M9.6 9.7a2.8 2.8 0 0 0 3.9 3.9" />
      <path d="M6.4 6.9C4 8.5 2.4 12 2.4 12s3.6 6.2 9.6 6.2c1.6 0 3-.45 4.2-1.1M17.9 15.1c1.9-1.6 3.1-3.1 3.1-3.1S17.4 5.8 12 5.8c-.72 0-1.4.09-2 .25" />
    </Icon>
  );
}

export function IconShield(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M12 3.4 4.8 6.2v5.4c0 4 3 7.6 7.2 9 4.2-1.4 7.2-5 7.2-9V6.2z" />
      <path d="m9 12 2.2 2.2L15.4 10" />
    </Icon>
  );
}

export function IconInbox(p: IconProps) {
  return (
    <Icon {...p}>
      <path d="M3.6 13.4h4l1.4 2.6h6l1.4-2.6h4" />
      <path d="M5.6 5.2h12.8l2 8.2v4a2 2 0 0 1-2 2H5.6a2 2 0 0 1-2-2v-4z" />
    </Icon>
  );
}
